/**
 * The app-side wiring of `recordDocSync.ts` (S305): signals, the edit
 * debounce, `doc_updated`, the backstop poll, the kill switch and the
 * persisted memory. Kept apart from the engine so the engine imports nothing
 * that needs a browser and a smoke can run it in node.
 */

import { createEffect, createSignal, on, onCleanup, type Accessor } from 'solid-js';
import { syncOnce, type DocStoreLike, type RecordAdapter, type SyncMemory, type SyncReport } from './recordDocSync.ts';
import { bridges, getConnection, connectionsVersion } from '../opbridge/store.ts';
import type { BridgeConfig } from '../opbridge/storage.ts';
import { performanceMode } from '../opbridge/performanceMode.ts';
import { onDocUpdated } from '../opbridge/consumerWs.ts';
import { pushToast } from '../ui/toast.ts';
import { watchJob } from '../diag/watchdog.ts';

const CONSUMER = 'opbuttons';
const DEBOUNCE_MS = 1500;
/** Backstop only: `doc_updated` is the fast path. Rig setup data, so slow. */
const POLL_MS = 30_000;

export type RecordSyncState = 'idle' | 'saving' | 'saved' | 'restored' | 'error';

export interface RecordSyncHandle {
  state: Accessor<RecordSyncState>;
  lastError: Accessor<string | null>;
  lastSyncedAt: Accessor<number | null>;
  skipped: Accessor<{ docId: string; reason: string }[]>;
  /** One pass now (connect, a `doc_updated`, the poll, the edit debounce). */
  tick(): Promise<SyncReport | null>;
}

/** The primary bridge if it exists and is connected (DEC-063: primary only).
 *  `void connectionsVersion()` is load-bearing: see projectSync.ts. */
function pickPrimaryConnected(): BridgeConfig | null {
  void connectionsVersion();
  const all = bridges();
  if (all.length === 0) return null;
  const primary = all.find((cfg) => cfg.is_primary) ?? all[0]!;
  const conn = getConnection(primary.id);
  return conn && conn.status() === 'connected' ? primary : null;
}

function loadMemory(key: string): SyncMemory {
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const m = JSON.parse(raw) as Partial<SyncMemory>;
      if (m && typeof m === 'object') {
        return { agreed: m.agreed ?? {}, stamps: m.stamps ?? {}, legacyDone: m.legacyDone === true, nodeId: m.nodeId ?? null };
      }
    }
  } catch { /* fall through */ }
  return { agreed: {}, stamps: {} };
}

/** Wire one adapter: debounced pass on every local edit, a pass on connect,
 *  on a `doc_updated` for one of its docs, and on a slow backstop poll;
 *  paused in performance mode (DEC-216) and by the kill switch. */
export function installRecordSync<L>(opts: {
  adapter: RecordAdapter<L>;
  read: Accessor<L>;
  write(next: L): void;
  /** localStorage key that, set to '1', makes this sync inert (the bot sets it). */
  disableKey: string;
  /** localStorage key holding this adapter's SyncMemory. */
  memoryKey: string;
  /** See `SyncIo.unauthoritative`. */
  unauthoritative?(): boolean;
}): RecordSyncHandle {
  const [state, setState] = createSignal<RecordSyncState>('idle');
  const [lastError, setLastError] = createSignal<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = createSignal<number | null>(null);
  const [skipped, setSkipped] = createSignal<{ docId: string; reason: string }[]>([]);
  const memory = loadMemory(opts.memoryKey);
  let inFlight = false;
  let again = false;
  let warnedHeld = false;
  let timer: number | null = null;
  let pollTimer: number | null = null;
  const disabled = () => { try { return localStorage.getItem(opts.disableKey) === '1'; } catch { return false; } };

  const tick = async (): Promise<SyncReport | null> => {
    if (disabled() || performanceMode()) return null;
    if (inFlight) { again = true; return null; }
    const cfg = pickPrimaryConnected();
    const conn = cfg ? getConnection(cfg.id) : undefined;
    if (!conn || conn.status() !== 'connected') return null;
    const client = conn.httpClient();
    const store: DocStoreLike = {
      list: async () => (await client.docList(CONSUMER)).docs,
      get: (id) => client.docGet(CONSUMER, id),
      put: (id, baseRev, body) => client.docPut(CONSUMER, id, { base_rev: baseRev, body }),
      del: (id) => client.docDelete(CONSUMER, id),
    };
    inFlight = true;
    watchJob(`record-sync:${opts.adapter.label}`);
    try {
      // Replacing an unedited seed overwrites nothing anyone made: no toast.
      const hadLocal = opts.adapter.split(opts.read()).size > 0 && opts.unauthoritative?.() !== true;
      const report = await syncOnce(store, opts.adapter, {
        read: opts.read,
        write: opts.write,
        memory,
        saveMemory: (m) => { try { localStorage.setItem(opts.memoryKey, JSON.stringify(m)); } catch { /* full: next pass rewrites */ } },
        now: () => Date.now(),
        ...(opts.unauthoritative ? { unauthoritative: opts.unauthoritative } : {}),
        nodeId: cfg?.nodeId ?? null,
        // A show starting mid-pass stops it (DEC-216: housekeeping waits).
        shouldStop: () => performanceMode() || disabled(),
      });
      setSkipped(report.skipped);
      if (report.held && !warnedHeld) {
        warnedHeld = true;
        pushToast('warn', `${cfg?.name ?? 'The default computer'} lists none of your ${opts.adapter.label} records. Nothing was deleted here; they sync again when it lists them.`, 10000);
      } else if (!report.held) warnedHeld = false;
      setLastError(null);
      setLastSyncedAt(Date.now());
      if (report.adopted > 0 || report.removedLocal > 0) {
        setState('restored');
        // DEC-081 honesty: automatic, never invisible. Not on a first fill.
        if (hadLocal) {
          const bits: string[] = [];
          if (report.adopted) bits.push(`${report.adopted} updated`);
          if (report.removedLocal) bits.push(`${report.removedLocal} removed`);
          pushToast('ok', `${opts.adapter.label} synced from another device - ${bits.join(', ')}.`, 5000);
        }
      } else if (report.pushed > 0 || report.deletedRemote > 0) setState('saved');
      else setState('idle');
      return report;
    } catch (err) {
      setLastError(err instanceof Error ? err.message : String(err));
      setState('error');
      return null;
    } finally {
      inFlight = false;
      // An edit or event that landed mid-pass gets its own pass.
      if (again) { again = false; setTimeout(() => { void tick(); }, 0); }
    }
  };

  createEffect(on(opts.read, () => {
    if (timer !== null) clearTimeout(timer);
    timer = window.setTimeout(() => { timer = null; void tick(); }, DEBOUNCE_MS);
  }, { defer: true }));

  const off = onDocUpdated((e) => {
    if (e.consumer !== CONSUMER) return;
    if (!opts.adapter.prefixes.some((p) => e.docId.startsWith(p))) return;
    // The echo of our own write: we already hold that rev.
    if (!e.deleted && memory.agreed[e.docId]?.rev === e.rev) return;
    void tick();
  });
  onCleanup(off);

  createEffect(on(() => pickPrimaryConnected()?.id ?? null, (id) => {
    if (pollTimer !== null) { clearInterval(pollTimer); pollTimer = null; }
    if (!id) return;
    void tick();
    pollTimer = window.setInterval(() => { void tick(); }, POLL_MS);
  }));

  return { state, lastError, lastSyncedAt, skipped, tick };
}
